/**
 * inputsV3StateRules.js — fonte unica de derivacao de estado de inputs V3.
 *
 * PA-006 (premissa do plano de revamp UI/UX): toda surface (chips,
 * donut, dashes, tile, MonthSelector, ProgressDrawer) deriva estado a
 * partir DESTE arquivo. Componentes nao decidem cor por conta propria.
 *
 * O que vive aqui:
 *   - Estados canonicos (D-33).
 *   - Cores canonicas (referenciam os tokens do material-dashboard-pro).
 *   - ``classifyInputAggregate(input)``  : winner-take-all (donut, badge).
 *   - ``inputSubstates(input)``           : substates granulares para os
 *                                           3 dashes do rodape.
 *   - ``hasAttentionSignal(input)``       : OR sobre subestados — usado
 *                                           pelo contador "em atencao"
 *                                           (A-001). Nao confundir com
 *                                           o bucket agregado.
 *   - ``classifyMonthAggregate(monthStatus)`` : status do mes -> bucket
 *                                               do MonthSelector.
 *   - ``classifyBlockState(block)``       : state cru de bloco -> bucket
 *                                           visual (BlocksPane / cockpit).
 *
 * Ao trocar regras, edite apenas este arquivo.
 */

import {
  dangerColor,
  grayColor,
  successColor,
  warningColor,
} from "../assets/jss/material-dashboard-pro-react";

// ---------------------------------------------------------------------------
// 1. Estados canonicos (D-33).
// ---------------------------------------------------------------------------

export const INPUT_STATES = Object.freeze([
  "system_error",
  "critical",
  "pending_decision",
  "attention",
  "processing",
  "in_progress_attention",
  "in_progress",
  "delivered_ok",
  "not_sent",
]);

// ``system_error`` (D-35) — falha tecnica genuina do pipeline. Diferente
// de ``critical`` (cliente precisa agir): aqui o sistema interrompeu o
// processamento. Preto comunica "interrompido / sem alternativa" sem
// ambiguidade com o vermelho operacional. Espelha
// ``MONTH_STATUS_LEGEND.system_error``, ``COLORS.system_error`` em
// ``StagePips.js`` e ``chipSystemError`` em ``inputs_components/styles.js``.
const SYSTEM_ERROR_BLACK = "#000000";

const PROCESSING_GRAY = grayColor[5];
const WAITING_TO_ADVANCE_GREEN = "#a5d6a7";
const WAITING_TO_ADVANCE_GREEN_ALT = "#c8e6c9";
// Laranja claro (Material orange 200) para xlinks parciais com aviso
// — paralelo perfeito do verde claro (Material green 200) do
// ``in_progress`` clean. Mantemos a familia laranja do
// ``warningColor[2]`` (warning solido) e nao migramos para amarelo,
// para que o usuario LEIA o estado como "warning suave em curso" e
// nao como "amarelo isolado" (que quebra a paleta consistente da UI).
const WAITING_TO_ADVANCE_ORANGE = "#ffcc80";
const PENDING_DECISION_ORANGE = "#ed6c02";

export const INPUT_STATE_COLORS = Object.freeze({
  system_error: SYSTEM_ERROR_BLACK,
  critical: dangerColor[2],
  pending_decision: PENDING_DECISION_ORANGE,
  attention: warningColor[2],
  processing: PROCESSING_GRAY,
  delivered_ok: successColor[3],
  in_progress: WAITING_TO_ADVANCE_GREEN,
  in_progress_attention: WAITING_TO_ADVANCE_ORANGE,
  not_sent: grayColor[5],
});

// ---------------------------------------------------------------------------
// 1.b Backgrounds (CSS ``background`` value) — pode ser cor solida OU
//     gradiente. Usado em LUGARES onde o estado pinta um QUADRADO/RETANGULO
//     (badges, celulas do overview, swatches da legenda).
//
// Diferenca em relacao a ``INPUT_STATE_COLORS``:
//   - ``INPUT_STATE_COLORS`` deve ser sempre uma COR solida (usado tambem
//     como ``color: ...`` para texto, ``stroke`` no donut, etc.).
//   - ``INPUT_STATE_BACKGROUNDS`` e' usado apenas como ``background:``
//     CSS — pode incluir gradientes para representar estados "entre dois
//     mundos".
//
// Caso especial — ``pending_decision`` recebe um padrao hachurado
// diagonal (warning amarelo + danger vermelho). A semantica visual:
// "esse arquivo esta entre 'em atencao' e 'rejeitado' — o pipeline esta
// bloqueado aguardando uma decisao humana". Diferencia visualmente de
// ``attention`` (amarelo solido) que era confundido com a cor anterior
// laranja (proxima do amarelo). Listras a 45° atraem mais atencao que
// uma cor solida.
// ---------------------------------------------------------------------------
export function pendingDecisionHatch(stripeLightPx = 4, stripeDarkPx = 8) {
  return `repeating-linear-gradient(
  -45deg,
  ${warningColor[2]} 0,
  ${warningColor[2]} ${stripeLightPx}px,
  ${dangerColor[2]} ${stripeLightPx}px,
  ${dangerColor[2]} ${stripeDarkPx}px
)`;
}

const _PENDING_DECISION_HATCHED = pendingDecisionHatch(4, 8);

/** Presets de hachurado por superficie (input pip / month pip). */
export const PENDING_DECISION_HATCH_BY_SIZE = Object.freeze({
  input: _PENDING_DECISION_HATCHED,
  month: _PENDING_DECISION_HATCHED,
});

export const INPUT_STATE_BACKGROUNDS = Object.freeze({
  system_error: SYSTEM_ERROR_BLACK,
  critical: dangerColor[2],
  pending_decision: _PENDING_DECISION_HATCHED,
  attention: warningColor[2],
  processing: PROCESSING_GRAY,
  delivered_ok: successColor[3],
  in_progress: WAITING_TO_ADVANCE_GREEN,
  in_progress_attention: WAITING_TO_ADVANCE_ORANGE,
  not_sent: grayColor[5],
});

// ---------------------------------------------------------------------------
// 1.c Background especial para a CAIXA da legenda "Aguardando para
//     avancar". Hoje a caixa e' um quadrado verde-claro solido. Com a
//     introducao de ``in_progress_attention`` (laranja claro), passamos
//     a representar AMBOS estados na mesma caixa via split diagonal:
//     metade superior-esquerda verde claro (in_progress sem aviso),
//     metade inferior-direita laranja claro (in_progress com aviso).
//
// O uso e SOMENTE no swatch de legenda (InputsV3 mouseover + Overview
// topo da tela). Cores reais dos cards continuam usando
// ``INPUT_STATE_COLORS`` / ``INPUT_STATE_BACKGROUNDS`` por estado.
// ---------------------------------------------------------------------------
export const IN_PROGRESS_LEGEND_BACKGROUND = `linear-gradient(135deg, ${WAITING_TO_ADVANCE_GREEN} 50%, ${WAITING_TO_ADVANCE_ORANGE} 50%)`;

export const PROCESSING_COLORS = Object.freeze({
  base: grayColor[10],
  highlight: grayColor[12],
});

export const IN_PROGRESS_COLORS = Object.freeze({
  primary: WAITING_TO_ADVANCE_GREEN,
  alternate: WAITING_TO_ADVANCE_GREEN_ALT,
});

export const INPUT_STATE_LABELS = Object.freeze({
  system_error: "Erro de processamento",
  critical: "Arquivo rejeitado",
  pending_decision: "Pergunta em aberto",
  attention: "Em atenção",
  processing: "Processando",
  delivered_ok: "OK",
  in_progress: "Aguardando para avançar",
  in_progress_attention: "Aguardando para avançar (com atenção)",
  not_sent: "Não enviado",
});

// i18n keys que espelham os fallbacks acima. Usados pelo
// ``inputStateLabel(state, t)`` — callsites React passam ``t`` e
// recebem a versao localizada (pt/en/es). Sem ``t``, caimos no
// fallback em ``INPUT_STATE_LABELS`` (PT-BR), preservando o contrato
// para callers fora do contexto React.
const INPUT_STATE_LABEL_KEYS = Object.freeze({
  system_error: "inputs_v3.input_state.system_error",
  critical: "inputs_v3.input_state.critical",
  pending_decision: "inputs_v3.input_state.pending_decision",
  attention: "inputs_v3.input_state.attention",
  processing: "inputs_v3.input_state.processing",
  delivered_ok: "inputs_v3.input_state.delivered_ok",
  in_progress: "inputs_v3.input_state.in_progress",
  in_progress_attention: "inputs_v3.input_state.in_progress_attention",
  not_sent: "inputs_v3.input_state.not_sent",
});

/**
 * Devolve o label localizado de um bucket de estado de input.
 * Aceita ``t`` (do ``react-i18next``) e cai no fallback PT-BR
 * (``INPUT_STATE_LABELS``) quando ``t`` nao e' passado — preserva o
 * uso fora do contexto React.
 */
export function inputStateLabel(state, t) {
  const key = INPUT_STATE_LABEL_KEYS[state];
  const fallback = INPUT_STATE_LABELS[state] || state || "";
  if (!key || typeof t !== "function") return fallback;
  return t(key, fallback);
}

// ---------------------------------------------------------------------------
// 1.d Labels para BLOCOS de calculo (status do BlockControlV3 derivado
//     pelo backend). Usado pelo BlocksPane do ProgressDrawer e por
//     qualquer lugar que renderize "estado do bloco" (PA-006).
//
// Nota terminol\u00f3gica (A-010): o backend continua falando ``stale``
// como nome canonico, mas a UI passa a falar "na fila" \u2014 termo mais
// executivo, refletindo o que o consultor espera ("o sistema vai
// recalcular"). O backend tamb\u00e9m converte ``stale`` em ``waiting``
// quando alguma depend\u00eancia do bloco n\u00e3o est\u00e1 pronta (ver
// ``_serialize_block_for_list``).
// ---------------------------------------------------------------------------
export const BLOCK_STATUS_LABELS = Object.freeze({
  done: "concluído",
  // Onda 3.1: bucket canonico passa a ser ``processing`` (alinhado a
  // CSL/BLK/InputControl). ``running`` permanece como compat defensiva
  // ate todos os snapshots cacheados serem invalidados.
  processing: "calculando",
  running: "calculando",
  waiting: "aguardando",
  failed: "falhou",
  stale: "na fila",
});

// ---------------------------------------------------------------------------
// 1.e Mapeamento de ``InputControlV3.state`` (cru) para r\u00f3tulo curto
//     + tonalidade. Usado pelo HistoryPane (linha por entrega) e por
//     qualquer pane que precise traduzir o estado bruto da entrega
//     (n\u00e3o confundir com o classifier global ``classifyInputAggregate``,
//     que considera xlinks, decis\u00f5es etc.).
//
// Tonalidades:
//   - ``ok``    -> verde s\u00f3lido
//   - ``warn``  -> amarelo
//   - ``err``   -> vermelho
//   - ``info``  -> cinza/azul neutro (sem informa\u00e7\u00e3o de status final)
// ---------------------------------------------------------------------------
// Mantemos ``label`` apenas como fallback (raw pt-BR) — o
// callsite deve passar ``t`` para obter a versao localizada via
// ``labelKey``. Isso permite que consumidores fora do React (ex.:
// services puros) ainda recebam algum texto legivel sem precisar
// carregar i18n.
const INPUT_CONTROL_STATE_LABELS = Object.freeze({
  success: {
    label: "OK",
    labelKey: "modeling_inputs_v3.input_control_state.ok",
    tone: "ok",
  },
  warning: {
    label: "Atenção",
    labelKey: "modeling_inputs_v3.input_control_state.attention",
    tone: "warn",
  },
  error: {
    label: "Rejeitado",
    labelKey: "modeling_inputs_v3.input_control_state.rejected",
    tone: "err",
  },
  system_error: {
    label: "Rejeitado",
    labelKey: "modeling_inputs_v3.input_control_state.rejected",
    tone: "err",
  },
  uploaded: {
    label: "Em andamento",
    labelKey: "modeling_inputs_v3.input_control_state.in_progress",
    tone: "info",
  },
  processing: {
    label: "Em andamento",
    labelKey: "modeling_inputs_v3.input_control_state.in_progress",
    tone: "info",
  },
});

export function inputControlStateLabel(state, t) {
  const entry = INPUT_CONTROL_STATE_LABELS[state];
  if (!entry) return { label: "—", tone: "info" };
  const label = typeof t === "function" ? t(entry.labelKey) : entry.label;
  return { label, tone: entry.tone };
}

// ---------------------------------------------------------------------------
// 2. Classificacao agregada (donut / badge / contador exclusivo).
// ---------------------------------------------------------------------------

/**
 * Mapeia um input para um dos seis buckets canonicos. Winner-take-all.
 * Espelha exatamente a logica que vivia (duplicada) em
 * ``InputBoxV3.classifyInputState`` e ``InputsV3.classifyInputState`` —
 * o objetivo desta consolidacao e' que ambas leiam DAQUI.
 */
export function classifyInputAggregate(input, opts = {}) {
  const { isUploading = false } = opts;
  if (isUploading) return "processing";

  const al = input?.attention_label;
  const cs = input?.control_state;

  // SINAL CANONICO DE "processando agora" vem do backend via
  // ``attention_label === 'processing'`` (definido em
  // ``_derive_runtime_state_inner`` quando ``control.state in
  // ('uploaded', 'processing')``). Usar ESTE sinal — em vez do otimismo
  // local de ``stages.data_source='running'`` — \u00e9 o que elimina a
  // oscila\u00e7\u00e3o do donut entre warning e listrado durante o
  // reprocessamento. O sinal vence QUALQUER resultado est\u00e1vel anterior
  // (warning, attention, in_progress, etc.) porque o cascade de
  // ``invalidate_dependents`` j\u00e1 zerou os artefatos dependentes \u2014 nada
  // do run anterior vale enquanto o pipeline est\u00e1 em curso.
  //
  // \u00danica exce\u00e7\u00e3o: erros hard que n\u00e3o foram disparados pelo
  // pr\u00f3prio reprocesso (ex.: control est\u00e1 em ``error`` E n\u00e3o
  // h\u00e1 job ativo). Esses continuam cr\u00edticos. Mas se o usu\u00e1rio
  // clicou reprocesso, o backend imediatamente coloca control.state =
  // 'uploaded' (n\u00e3o mais 'error') e o caminho ``processing`` ganha.
  if (al === "processing" || cs === "uploaded" || cs === "processing") {
    return "processing";
  }

  // ``processing`` em ``data_source``/``data_structure`` — INPUT em
  // parse (caminho legacy/otimismo local). Mantido como defesa em
  // profundidade caso o snapshot do backend ainda nao tenha sido
  // refrescado e o front tenha marcado ``data_source='processing'`` via
  // ``updateInputStatusOptimistically``. Onda 3.1: ``"running"`` foi
  // unificado em ``"processing"``; ambos sao aceitos durante a
  // transicao para compat com snapshots cacheados.
  //
  // Onda 1.7 (correcao): ``s.cross_linking === "running"`` foi
  // REMOVIDO deste short-circuit. Antes, qualquer xlink em execucao
  // (worker rodando) forcava o pip do input para ``loading`` (cinza
  // shimmer), mascarando o estado real do INPUT (que pode estar
  // entregue+OK ou com warnings de outros xlinks ja rodados). Bug
  // visivel em CCU/Argentina: ``custodia_koner`` aparecia cinza
  // shimmer (loading) apesar do arquivo estar entregue. O sinal "tem
  // xlink rodando agora" e' overlay, nao cor base — ja vive em
  // ``xlinks_in_flight`` + ``classifyInputPip.hasInFlightOverlay``.
  const s = input?.stages || {};
  if (
    s.data_source === "processing" ||
    s.data_source === "running" ||
    s.data_structure === "processing" ||
    s.data_structure === "running"
  ) {
    return "processing";
  }

  // Erros hard SEMPRE vencem permanent/waived. Defesa contra payload
  // inconsistente em que o backend envia
  // ``{is_permanent_for_month=true, attention_label='critical'}`` ao
  // mesmo tempo — caso classico: input ``monthly`` com
  // ``reuse_auto=true`` (ex-``semi_fixed``) e ``has_prior_success``
  // que sofreu rejeicao explicita de upload no mes corrente. Sem
  // este short-circuit, o curto-circuito original em
  // ``is_permanent_for_month`` pintava o card verde apesar do erro
  // hard.
  //
  // Ordem de prioridade (rev. 2026-05-26 — diretiva consultor):
  //   1. system_error (falha tecnica do pipeline)        → preto
  //   2. pending_decision (qualquer D-22 aberta no input) → hachurado
  //   3. critical (erro consolidado sem decisao pendente) → vermelho
  //
  // Onda 5.x (D-35 estendido a inputs): ``system_error`` ganha bucket
  // proprio em vez de colapsar em ``critical``. Falha tecnica grave
  // do pipeline tem prioridade absoluta — preto comunica "interrompido,
  // sem alternativa".
  if (
    cs === "system_error" ||
    s.data_structure === "system_error" ||
    s.cross_linking === "system_error"
  ) {
    return "system_error";
  }
  // ``pending_decision`` agora VENCE ``critical``: quando o input tem
  // pelo menos uma D-22 aberta (qualquer xlink em que ele participa
  // entrou em ``pending_decision``), o sinal dominante e' "tem
  // pergunta humana aguardando resposta" — mesmo que exista outro
  // xlink em ``error`` puro no mesmo input. A justificativa e' UX:
  // o consultor precisa responder a pergunta para destravar o
  // pipeline; o erro tecnico paralelo continua visivel na timeline e
  // nos demais xlinks, mas nao mascara a chamada para acao humana.
  //
  // ``system_error`` continua acima dessa regra porque indica falha
  // do pipeline em si (nao adianta responder pergunta enquanto o
  // sistema esta interrompido).
  if (
    s.data_structure === "pending_decision" ||
    s.cross_linking === "pending_decision"
  ) {
    return "pending_decision";
  }
  if (al === "critical" || cs === "error") {
    return "critical";
  }
  if (
    s.data_source === "error" ||
    s.data_structure === "error" ||
    s.cross_linking === "error"
  ) {
    return "critical";
  }
  // Onda 1.7 (correcao apos D-MTX-1):
  //
  // Antes desta mudanca, ``is_permanent_for_month`` era checado AQUI
  // (logo apos os erros hard) e retornava ``delivered_ok`` (verde
  // escuro) imediatamente — sem olhar warnings nem xlinks pendentes.
  // Isso produziu dois bugs reportados em CCU/Argentina:
  //
  //   1. ``venta_bruta`` mostrava verde escuro apesar de varios xlinks
  //      ja terem rodado com warning (``s.cross_linking ==
  //      'partial_warning'``) — o usuario via "OK" enquanto o painel
  //      de notas exibia avisos.
  //
  //   2. ``custodia_koner`` mostrava verde escuro apesar dos xlinks
  //      ainda nao terem rodado (``xlinks_in_flight > 0``) — o usuario
  //      via "concluido" enquanto na verdade ainda havia trabalho
  //      pendente.
  //
  // Solucao: warnings/partials/xlinks-em-voo VENCEM ``is_permanent_for_month``.
  // ``delivered_ok`` (verde escuro) e' reservado para "TODOS os
  // veredictos finalizados E sem nada pendente".
  //
  //   - ``s.cross_linking === 'warning'`` -> "attention" (amarelo solido)
  //   - ``s.cross_linking === 'partial_warning'`` -> "in_progress_attention"
  //   - ``s.cross_linking === 'partial'`` -> "in_progress"
  //   - ``xlinks_in_flight > 0`` (mesmo sem partial agregado) ->
  //     "in_progress" — protege o caso "input entregue, xlinks ainda
  //     nem foram dispatched / todos pending".
  //
  // ``s.data_structure === 'warning'`` tambem entra em "attention" —
  // structurally a entrega foi aceita mas com ressalvas que o
  // consultor precisa acolher (independente de permanencia).
  if (s.cross_linking === "warning" || s.data_structure === "warning") {
    return "attention";
  }
  if (s.cross_linking === "partial_warning") {
    return "in_progress_attention";
  }
  // Onda 1.7: ``partial`` com max_severity >= 2 (warning) tambem cai
  // em ``in_progress_attention``. Defesa contra inconsistencia
  // backend onde o post-processing agregou ``state="partial"`` (so
  // checa _has_success_clean + _has_incomplete) mas as notes ja
  // tem warnings registrados (max_severity >= 2). Bug observado em
  // CCU/Argentina: ``venta_bruta`` aparecia verde claro
  // (in_progress) apesar de varios xlinks ja terem rodado com
  // warning. Sem este check, o usuario via "OK parcial" enquanto
  // havia notas de atencao nas tabs.
  const xlMaxSev = Number(input?.cross_linking?.max_severity || 0);
  if (s.cross_linking === "partial" && xlMaxSev >= 2) {
    return "in_progress_attention";
  }
  if (s.cross_linking === "partial") {
    return "in_progress";
  }
  // Xlinks rodando agora ou aguardando dispatch — usa o helper
  // CANONICO ``isInputInFlight`` (cf. final do arquivo). Mesmo
  // gatilho usado em ``classifyInputPip`` para decidir se anima o
  // ripple — garante que NUNCA temos "anima sem cor base in_progress"
  // (bug observado em CCU/Argentina: ``custodia_koner`` cinza +
  // shimmer porque aggregate caia em ``not_sent`` enquanto pip
  // disparava overlay).
  //
  // Defesa max_severity: se ja tem warning registrado nas notes
  // (max_severity >= 2), sobe para ``in_progress_attention`` em vez
  // de ``in_progress`` puro.
  if (isInputInFlight(input)) {
    if (xlMaxSev >= 2) return "in_progress_attention";
    return "in_progress";
  }

  // Inputs opcionais marcados como concluidos sem arquivo
  // entram aqui (``is_waived === true``). Backend tambem marca
  // ``is_permanent_for_month=true`` nesses casos.
  if (input?.is_waived) return "delivered_ok";

  // Onda 5.x (2026-05-27): input entregue mas com cruzamentos AGUARDANDO
  // contrapartes NAO deve cair em ``delivered_ok`` (verde-escuro). A
  // sinalizacao correta e' verde-claro (``in_progress`` =
  // "esperando para avancar"), ou ``in_progress_attention`` se ja ha
  // severidade de warning registrada.
  //
  // Cenarios cobertos AQUI que NAO sao pegos por ``isInputInFlight``:
  //   - ``cross_linking.state === "blocked"``: CSL bloqueado aguardando
  //     upload da contraparte. ``isInputInFlight`` exclui ``blocked``
  //     de proposito (nao e' trabalho ativo, nao deve animar ripple),
  //     mas para cor base, "esperando contraparte" ainda e' "esperando
  //     para avancar".
  //   - ``cross_linking.state in (null, undefined)`` com ``count > 0``:
  //     xlinks declarados pelo descriptor sem nenhum control no mes
  //     (contrapartes ainda nem comecaram).
  //   - ``stages.cross_linking === "waiting"``: projecao do adapter
  //     para os dois casos acima.
  //
  // Pre-condicao ``dsDelivered`` (parse OK) protege contra o caso
  // "input ainda nao foi enviado" — la o ``cross_linking=waiting``
  // significa "nem chegamos nessa etapa", nao "aguardando avancar".
  //
  // Vence ``is_permanent_for_month`` (mesmo padrao das regras de
  // warning/partial/in-flight acima). NAO vence ``is_waived`` —
  // waive e' decisao explicita do usuario que sela o card.
  const xlState = input?.cross_linking?.state;
  const xlCount = Number(input?.cross_linking?.count || 0);
  const dsDelivered =
    s.data_structure === "success" || s.data_structure === "warning";
  const xlinksAwaitingCounterparts =
    s.cross_linking === "waiting" ||
    (xlCount > 0 &&
      (xlState == null || xlState === "blocked" || xlState === "pending"));
  if (dsDelivered && xlinksAwaitingCounterparts) {
    if (xlMaxSev >= 2) return "in_progress_attention";
    return "in_progress";
  }

  // ``is_permanent_for_month`` so entra agora — depois de termos
  // descartado warning/partial/in-flight/xlinks-aguardando. Verde
  // escuro = "tudo realmente concluido".
  if (input?.is_permanent_for_month) return "delivered_ok";

  if (s.data_source === "success" && s.cross_linking === "success") {
    return "delivered_ok";
  }

  // Fallback defensivo via ``phase`` consolidada do adapter quando
  // ``stages`` nao foi populado (ex.: cards montados manualmente,
  // storybook). ``phase === "ok"`` foi visto cair aqui em casos onde
  // ``cross_linking === "waiting"`` ja foi tratado acima — chega aqui
  // so quando ``stages`` esta vazio mesmo.
  const phase = input?.phase;
  if (phase === "permanent" || phase === "ok") return "delivered_ok";
  if (phase === "warning") return "attention";
  if (phase === "pending_decision") return "pending_decision";
  if (phase === "system_error") return "system_error";
  if (phase === "blocked") return "critical";
  if (phase === "processing") return "processing";
  if (phase === "queued") return "in_progress";
  if (phase === "pending" || phase === "not_sent") return "not_sent";

  return "not_sent";
}

// ---------------------------------------------------------------------------
// 2.b Pip unico do InputBoxV3 (substitui os 3 dashes do rodape).
// ---------------------------------------------------------------------------

/**
 * Reduz o agregado granular do input a UM estado de pip — o que o
 * usuario precisa olhar primeiro no card. O tooltip do InputBox
 * continua expondo a granularidade dos 3 stages; aqui estamos so
 * decidindo a "cor sintese" do rodape.
 *
 * Mapeamento (alinhado 1:1 com a paleta da legenda do
 * ``OverviewV3`` — ``INPUT_STATE_BACKGROUNDS`` + 5 itens da
 * legenda):
 *
 *   critical          -> "error"            (vermelho solido)
 *   pending_decision  -> "pending_decision" (hachurado amarelo+vermelho)
 *   processing        -> "loading"          (cinza animado — algo
 *                                            acontecendo agora)
 *   in_progress       -> "partial"          (verde claro — aguardando
 *                                            para avancar)
 *   attention         -> "warning"          (amarelo solido)
 *   delivered_ok      -> "success"          (verde solido)
 *   not_sent          -> "waiting"          (cinza solido)
 *
 * Sub-bucket ``partial`` (xlinks parcialmente concluidos sem
 * apontamento) continua caindo em ``"success"`` aqui: o status
 * explicitamente novo na legenda e' ``in_progress`` / "Aguardando
 * para avancar". A granularidade do xlink parcial segue presente nos
 * 3 stages do tooltip do InputBox.
 *
 * ``opts.isLoading``: forca "loading" (shimmer cinza) quando o
 * frontend ainda nao tem o estado real do input. Usado em transicoes
 * de snapshot/refresh.
 */
export function inputSinglePipState(input, opts = {}) {
  if (opts && opts.isLoading) return "loading";
  const bucket = classifyInputAggregate(input, opts);
  switch (bucket) {
    case "system_error":
      // ``StagePips.COLORS.system_error`` ja pinta preto — basta
      // propagar o nome do estado direto.
      return "system_error";
    case "critical":
      return "error";
    case "pending_decision":
      return "pending_decision";
    case "processing":
      return "loading";
    case "in_progress":
      return "partial";
    case "in_progress_attention":
      return "partial_warning";
    case "attention":
      return "warning";
    case "delivered_ok":
      return "success";
    case "not_sent":
    default:
      return "waiting";
  }
}

/**
 * D-MTX-1 (Onda 1.8 — refeita): helper CANONICO compartilhado por
 * ``classifyInputAggregate`` (define se cor base = in_progress) e
 * ``classifyInputPip`` (define se anima o ripple). Mesmo gatilho ->
 * impossivel ter "ripple sem cor base in_progress" ou "cor in_progress
 * sem ripple".
 *
 * Gatilho:
 *   inFlight = (data_structure in {success, warning})    // entregue
 *           && (xlinks_in_flight > 0 || cross_linking in {running, pending})
 *
 * Pre-condicao ``data_structure`` settled (success/warning) protege
 * contra inputs ainda nao entregues — ``cross_linking === "waiting"``
 * em input nao entregue NAO significa "tem xlink pendente", significa
 * "ainda nem chegou nessa etapa".
 */
export function isInputInFlight(input) {
  const s = input?.stages || {};
  const dsDelivered =
    s.data_structure === "success" || s.data_structure === "warning";
  if (!dsDelivered) return false;
  const xlinksInFlight = Number(input?.xlinks_in_flight || 0) > 0;
  // ``cross_linking.state`` e' o estado bruto do backend
  // (pending/processing/success/...). ``stages.cross_linking`` e' a
  // projecao mapeada (usa "waiting" para pending) — usamos so como
  // fallback caso ``input.cross_linking`` nao venha. Onda 3.1:
  // ``"running"`` foi unificado em ``"processing"``; ambos sao
  // aceitos durante a transicao.
  const xlRaw = input?.cross_linking?.state;
  const xlActive =
    xlRaw === "processing" || xlRaw === "running" || xlRaw === "pending";
  return xlinksInFlight || xlActive;
}

/**
 * Classifica o input em ``{state, hasInFlightOverlay}``.
 *
 * - ``state``: mesma string que ``inputSinglePipState`` retorna (cor
 *   base do pip).
 * - ``hasInFlightOverlay``: ``true`` quando ``isInputInFlight`` E o
 *   estado base permite o efeito (nao e' loading/processing/error/
 *   pending_decision — esses ja transmitem mensagem propria forte).
 *
 * O efeito (ripple/onda horizontal sobre a cor base, igual ao chip
 * "Processando" do header) e' aplicado SOMENTE no pip e no donut
 * pelo ``StagePips`` e ``InputBoxV3.Donut``. Badge nao recebe.
 */
export function classifyInputPip(input, opts = {}) {
  const state = inputSinglePipState(input, opts);
  const overlayBlocked =
    state === "loading" ||
    state === "processing" ||
    state === "error" ||
    state === "system_error" ||
    state === "pending_decision";
  return {
    state,
    hasInFlightOverlay: isInputInFlight(input) && !overlayBlocked,
  };
}

// ---------------------------------------------------------------------------
// 3. Substates granulares (3 dashes do rodape do tile).
// ---------------------------------------------------------------------------

/**
 * Devolve o estado de cada uma das 3 etapas do input em sua granularidade
 * crua. Usado pela renderizacao dos 3 dashes (data_source /
 * data_structure / cross_linking) e pela funcao ``hasAttentionSignal``.
 *
 * Diferente de ``classifyInputAggregate``: aqui NAO ha "winner" — devolve
 * o estado de cada eixo independentemente.
 */
export function inputSubstates(input) {
  const s = input?.stages || {};
  return {
    data_source: s.data_source || null,
    data_structure: s.data_structure || null,
    cross_linking: s.cross_linking || null,
  };
}

// ---------------------------------------------------------------------------
// 4. ``hasAttentionSignal`` — OR sobre subestados (A-001).
// ---------------------------------------------------------------------------

/**
 * Um input tem "sinal amarelo" se QUALQUER substate dele esta em
 * ``warning``. Tipicamente alimenta o chip "em atencao" do contador da
 * view principal (A-001).
 *
 * Diferente de ``classifyInputAggregate(...) === 'attention'``: aquele
 * e' o bucket exclusivo (winner-take-all). Aqui detectamos amarelo
 * mesmo quando o bucket dominante e' outro (ex.: ``critical`` com um
 * cross_linking warning ainda conta como atencao).
 */
export function hasAttentionSignal(input) {
  const sub = inputSubstates(input);
  return (
    sub.data_source === "warning" ||
    sub.data_structure === "warning" ||
    sub.cross_linking === "warning"
  );
}

// ---------------------------------------------------------------------------
// 4.b ``isInputDelivered`` — regra do contador "Entregue" (PA-010).
// ---------------------------------------------------------------------------

/**
 * Define se um input ja conta como "Entregue" para o contador do
 * topo da pagina. Regra (definida pelo consultor 2026-04-30):
 *
 *   Entregue ⇔ parsing terminou sem erros E nao ha xlink critico
 *              nem pergunta em aberto.
 *
 * Casos canonicos:
 *   - acabou de subir, ainda parseando ............... NAO entregue
 *   - parsing rodando ................................ NAO entregue
 *   - parsing OK, xlinks nao executaram ainda ........ entregue
 *   - parsing OK + 1+ xlink em "atencao" (warning) ... entregue
 *   - parsing OK + 1+ xlink "critico"/"pergunta" ..... NAO entregue
 *   - input semi-fixo/permanente do mes .............. entregue
 *
 * "sem erros" significa que ``data_structure`` virou ``success`` ou
 * ``warning`` (warning = passou com ressalvas; nao bloqueia
 * downstream). ``running``, ``waiting``, ``pending_decision``,
 * ``error`` e ``system_error`` quebram a entrega.
 */
export function isInputDelivered(input) {
  if (!input) return false;
  // Inputs opcionais waived contam como entregues (o backend tamb\u00e9m
  // marca ``is_permanent_for_month=true`` nesse caso, mas mantemos a
  // checagem expl\u00edcita por robustez de evolu\u00e7\u00e3o do shape).
  if (input.is_waived) return true;
  if (input.is_permanent_for_month) return true;

  const s = input.stages || {};
  const ds = s.data_structure;
  const xl = s.cross_linking;

  // Parsing precisa ter concluido sem erro nem pergunta em aberto.
  if (ds !== "success" && ds !== "warning") return false;

  // Cross-linking nao pode estar em estado bloqueante.
  if (xl === "error" || xl === "system_error" || xl === "pending_decision") {
    return false;
  }

  return true;
}

// ---------------------------------------------------------------------------
// 5. Mes -> bucket visual do MonthSelector / overview.
// ---------------------------------------------------------------------------

// Mapa: status do mes (vindo do backend ``_compute_month_status_v3``)
// -> bucket canonico que o MonthSelectorV3 entende.
//
// Vocabulario unificado (PA-006 + PA-009 + D-35):
//   - ``done``         — concluido (verde escuro).
//   - ``stale``        — na fila pra rerodar (verde claro). Tambem
//                        e' o nome interno em ``BlockControlV3.state``.
//   - ``action``       — cliente precisa agir agora — input rejeitado
//                        por validacao, decisao pendente, etc. Vermelho.
//   - ``system_error`` — falha tecnica genuina do pipeline. Vermelho
//                        mais escuro/grave; copy "erro de processamento".
//                        Antes colapsava com ``action`` (D-37, leva 96).
//   - ``ready``        — inputs prontos, calculo manual pendente
//                        (amarelo). Antes ``pending``.
//   - ``processing``   — calculo em curso (azul).
//   - ``waiting``      — aguardando entregas (cinza).
//   - ``loading``      — frontend ainda nao recebeu agregados
//                        (shimmer cinza).
//
// "Na fila" e' o label de UX do bucket interno ``stale`` (DA-002 +
// A-010 — preservado por compatibilidade com o backend).
//
// Onda 1.3: as cores referenciam os tokens do tema (em vez de hex
// literais que duplicavam — e divergiam entre — os mappings de
// ``DrawerHeader.MONTH_STATUS_COLOR`` (vocabulario V3 cru:
// ``ready``/``ready_to_calculate``/...) e
// ``ProgressDrawerHeader.MONTH_STATUS_COLOR`` (vocabulario V2 cru:
// ``done``/``action``/``pending``/...). Agora ambos consomem
// ``monthStatusColor()`` exportado abaixo, garantindo paleta unica.
export const MONTH_STATUS_LEGEND = Object.freeze({
  done: { color: successColor[3], label: "Concluído" },
  stale: { color: successColor[1], label: "Na fila" },
  action: { color: dangerColor[2], label: "Ação necessária" },
  // ``system_error`` mantem preto (D-35, leva 96): comunica
  // "interrompido / sem alternativa" sem ambiguidade com o vermelho
  // operacional. Espelha COLORS.system_error em StagePips.js e
  // chipSystemError em inputs_components/styles.js.
  system_error: { color: "#000000", label: "Erro de processamento" },
  ready: { color: warningColor[2], label: "Pronto para cálculo" },
  waiting: { color: grayColor[5], label: "Aguardando inputs" },
  processing: { color: successColor[0], label: "Processando" },
  loading: { color: grayColor[8], label: "Carregando…" },
});

// i18n keys que espelham os labels (e os complementos qualificadores
// ``reason`` que ``monthStateLabel`` distingue). Consumidos pelo
// proprio ``monthStateLabel`` quando ``t`` for fornecido.
const MONTH_STATUS_LABEL_KEYS = Object.freeze({
  done: "inputs_v3.month_status.done",
  stale: "inputs_v3.month_status.stale",
  action: "inputs_v3.month_status.action",
  system_error: "inputs_v3.month_status.system_error",
  ready: "inputs_v3.month_status.ready",
  waiting: "inputs_v3.month_status.waiting",
  processing: "inputs_v3.month_status.processing",
  loading: "inputs_v3.month_status.loading",
  // Qualificadores ``processing/<reason>`` e ``waiting/<reason>``.
  processing_crosslinks: "inputs_v3.month_status.processing_crosslinks",
  processing_blocks: "inputs_v3.month_status.processing_blocks",
  waiting_crosslinks: "inputs_v3.month_status.waiting_crosslinks",
  no_inputs: "inputs_v3.month_status.no_inputs",
});

/**
 * Cor canonica do bucket de status do mes. Aceita tanto o status cru
 * do backend (``complete``, ``ready_to_calculate``, ``action_needed``,
 * ``system_error``, ``processing``, ``waiting``, ``stale``, ``null``)
 * quanto o bucket ja classificado (``done``, ``ready``, ``action``,
 * ``processing``, ``waiting``, ``stale``, ``system_error``, ``loading``).
 *
 * Onda 1.3: substitui os mappings ``MONTH_STATUS_COLOR`` locais
 * triplicados em ``DrawerHeader.js`` e ``ProgressDrawerHeader.js``
 * (que estavam fora de sincronia entre si — um usava vocabulario V2,
 * outro V3, e ainda divergiam de ``MONTH_STATUS_LEGEND``).
 */
export function monthStatusColor(stateOrStatus) {
  const bucket =
    MONTH_STATUS_LEGEND[stateOrStatus] != null
      ? stateOrStatus
      : classifyMonthAggregate(stateOrStatus);
  return (
    MONTH_STATUS_LEGEND[bucket]?.color || MONTH_STATUS_LEGEND.waiting.color
  );
}

/**
 * Mapeia o ``month_status`` cru do backend (``_compute_month_status_v3``)
 * para o bucket visual do MonthSelectorV3. Substitui a antiga
 * ``statusV2toV3`` que vivia espalhada em ``MonthSelectorV3.js``.
 *
 * Aceita ``null``/``undefined`` (loading state placeholder enquanto
 * o backend ainda nao publicou agregados).
 *
 * D-35 (leva 96): o backend nunca mais retorna ``error`` cru — agora
 * separa em ``system_error`` (falha tecnica do pipeline) e
 * ``action_needed`` (cliente precisa agir). O case ``error`` permanece
 * aqui apenas como fallback defensivo (snapshots/aggregates antigos
 * cacheados no client) — mapeia para ``action`` por seguranca, ja que
 * historicamente representava majoritariamente input rejeitado.
 */
export function classifyMonthAggregate(monthStatus) {
  switch (monthStatus) {
    case "complete":
      return "done";
    case "stale":
      // Bucket interno preserva o nome ``stale`` (DA-002). O label
      // do usuario ("Na fila") esta em ``MONTH_STATUS_LEGEND``.
      return "stale";
    case "system_error":
      return "system_error";
    case "action_needed":
    case "error": // legacy: backend antigo colapsava em ``error``.
      return "action";
    case "ready_to_calculate":
      return "ready";
    case "processing":
      return "processing";
    case null:
    case undefined:
      return "loading";
    case "waiting":
    case "verifying":
    case "unknown":
    case "running":
    default:
      return "waiting";
  }
}

/**
 * Label canonico ("Concluído", "Na fila", etc.) para um bucket de
 * mes ja classificado (ou cru — passa pelo ``classifyMonthAggregate``
 * antes de procurar o label).
 *
 * D-MTX-2: aceita ``reason`` opcional para contextualizar buckets
 * que admitem mais de um motivo (``processing/blocks`` vs
 * ``processing/crosslinks``, ``waiting/inputs`` vs ``waiting/no_inputs``).
 *
 * I18n: o terceiro parametro opcional ``t`` (do ``react-i18next``)
 * habilita a versao localizada. Sem ``t`` (callers fora de React,
 * services puros, snapshots cacheados) caimos no fallback PT-BR —
 * mesmo contrato historico, sem quebra de chamada existente.
 */
export function monthStateLabel(stateOrStatus, reason = null, t = null) {
  const bucket =
    MONTH_STATUS_LEGEND[stateOrStatus] != null
      ? stateOrStatus
      : classifyMonthAggregate(stateOrStatus);

  const tx = (key, fallback) =>
    typeof t === "function" ? t(key, fallback) : fallback;

  if (bucket === "processing") {
    if (reason === "crosslinks") {
      return tx(
        MONTH_STATUS_LABEL_KEYS.processing_crosslinks,
        "Processando cruzamentos",
      );
    }
    if (reason === "blocks") {
      return tx(
        MONTH_STATUS_LABEL_KEYS.processing_blocks,
        "Processando cálculos",
      );
    }
    return tx(MONTH_STATUS_LABEL_KEYS.processing, "Processando");
  }
  if (bucket === "waiting") {
    if (reason === "no_inputs") {
      return tx(MONTH_STATUS_LABEL_KEYS.no_inputs, "Sem inputs");
    }
    if (reason === "crosslinks") {
      return tx(
        MONTH_STATUS_LABEL_KEYS.waiting_crosslinks,
        "Aguardando cruzamentos",
      );
    }
    return tx(MONTH_STATUS_LABEL_KEYS.waiting, "Aguardando inputs");
  }
  const key = MONTH_STATUS_LABEL_KEYS[bucket];
  const fallback = MONTH_STATUS_LEGEND[bucket]?.label || "Aguardando inputs";
  return tx(key, fallback);
}

// ---------------------------------------------------------------------------
// 5.b Atores rodando agora (Onda 1.8c).
// ---------------------------------------------------------------------------
//
// Pedido do usuario: "incluir ao lado do badge de progresso da
// modelagem o que exatamente esta sendo processado naquele momento".
// Backend exporta ``month_status_active`` (cf.
// ``_compute_month_status_active_v3``) com 3 listas: ``parses``
// (inputs em parse), ``crosslinks`` (xlinks com seus participantes)
// e ``blocks`` (blocos em execucao). Aqui escolhemos UMA categoria
// (a mais relevante dado o ``reason`` do badge) e formatamos uma
// frase curta — algo que cabe ao lado do chip sem quebrar layout.
//
// Heuristica:
//   - ``reason === "blocks"``     -> blocks > crosslinks > parses
//   - ``reason === "crosslinks"`` -> crosslinks > parses > blocks
//   - default                      -> parses > crosslinks > blocks
//
// Multiplas linhas seriam ruido; o ``ProgressDrawer`` ja tem timeline
// com TUDO que esta acontecendo. Aqui no chip damos so o "fato
// principal" deste tick.

function _joinNames(names, t) {
  if (!Array.isArray(names) || names.length === 0) return "";
  if (names.length === 1) return String(names[0]);
  const conj =
    typeof t === "function" ? t("inputs_v3.month_status.actor_and", "e") : "e";
  if (names.length === 2) return `${names[0]} ${conj} ${names[1]}`;
  // 3+ — listar so os 2 primeiros + "+N mais" para nao explodir o
  // chip.
  const extra = names.length - 2;
  const more =
    typeof t === "function"
      ? t("inputs_v3.month_status.actor_more", {
          count: extra,
          defaultValue: "+{{count}} mais",
        })
      : `+${extra} mais`;
  return `${names[0]}, ${names[1]} ${more}`;
}

function _formatParses(parses, t) {
  if (!parses || parses.length === 0) return null;
  const names = parses.map((p) => p.label || p.meta_name).filter(Boolean);
  if (names.length === 0) return null;
  const verb =
    typeof t === "function"
      ? t("inputs_v3.month_status.actor_reading", "Lendo")
      : "Lendo";
  if (names.length === 1) return `${verb} ${names[0]}…`;
  return `${verb} ${_joinNames(names, t)}…`;
}

function _formatCrosslinks(crosslinks, t) {
  if (!crosslinks || crosslinks.length === 0) return null;
  const verb =
    typeof t === "function"
      ? t("inputs_v3.month_status.actor_crosslinking", "Cruzando")
      : "Cruzando";
  // 1 xlink rodando: tentamos narrar pelos participantes
  // ("Cruzando A e B…"). Se nao temos os participantes, caimos no
  // proprio nome do xlink ("Cruzando csl_xyz…").
  if (crosslinks.length === 1) {
    const xl = crosslinks[0];
    const inputs = (xl.inputs || [])
      .map((i) => i.label || i.meta_name)
      .filter(Boolean);
    if (inputs.length >= 2) {
      return `${verb} ${_joinNames(inputs, t)}…`;
    }
    return `${verb} ${xl.label || xl.meta_name}…`;
  }
  // Multiplos xlinks rodando — listamos pelos nomes dos xlinks.
  const names = crosslinks.map((x) => x.label || x.meta_name).filter(Boolean);
  if (names.length === 0) return null;
  return `${verb} ${_joinNames(names, t)}…`;
}

function _formatBlocks(blocks, t) {
  if (!blocks || blocks.length === 0) return null;
  const names = blocks.map((b) => b.label || b.meta_name).filter(Boolean);
  if (names.length === 0) return null;
  // Onda 2026-05-17: o prefixo "Calculando" foi removido — a frase
  // ficava redundante quando o headline ja era "Processando cálculos"
  // ("Processando cálculos · Calculando PnL Logística…"). O complement
  // entrega so o nome do bloco (ou nomes), e o headline cuida do verbo.
  if (names.length === 1) return `${names[0]}…`;
  return `${_joinNames(names, t)}…`;
}

/**
 * Devolve uma frase curta narrando o ator principal em execucao no
 * mes selecionado. Retorna ``null`` quando nao ha nada rodando (ou
 * quando ``active`` nao foi enviado pelo backend).
 *
 * Parametros:
 *   - ``active``: payload ``{parses, crosslinks, blocks}`` do
 *     backend (campo ``month_status_active``).
 *   - ``reason``: ``month_status_reason`` do badge — guia da
 *     prioridade entre as 3 categorias.
 */
export function formatActiveActorsLabel(active, reason = null, t = null) {
  if (!active || typeof active !== "object") return null;
  const parses = Array.isArray(active.parses) ? active.parses : [];
  const crosslinks = Array.isArray(active.crosslinks) ? active.crosslinks : [];
  const blocks = Array.isArray(active.blocks) ? active.blocks : [];

  const formatters = {
    parses: () => _formatParses(parses, t),
    crosslinks: () => _formatCrosslinks(crosslinks, t),
    blocks: () => _formatBlocks(blocks, t),
  };

  let order;
  if (reason === "blocks") order = ["blocks", "crosslinks", "parses"];
  else if (reason === "crosslinks") {
    order = ["crosslinks", "parses", "blocks"];
  } else order = ["parses", "crosslinks", "blocks"];

  for (const key of order) {
    const txt = formatters[key]();
    if (txt) return txt;
  }
  return null;
}

// ---------------------------------------------------------------------------
// 6. Block state -> bucket visual.
// ---------------------------------------------------------------------------

const BLOCK_BUCKET_LABELS = Object.freeze({
  done: "Concluído",
  // Onda 3.1: bucket canonico ``processing`` (era ``running``).
  // Mantemos ``running`` como alias defensivo para snapshots cacheados.
  processing: "Em execução",
  running: "Em execução",
  waiting: "Aguardando",
  queued: "Na fila",
  failed: "Falhou",
  error: "Falhou",
});

/**
 * Devolve um bucket simplificado (done / processing / waiting / queued
 * / failed) a partir do estado bruto do ``BlockControlV3``. Mesma
 * semantica usada pela ``BlocksPane`` / ``CockpitStrip``.
 */
export function classifyBlockState(block) {
  const state = block?.state || block?.status;
  if (state === "success" || state === "warning") return "done";
  // Onda 3.1: ``BlockControlV3.state`` agora usa ``processing`` (era
  // ``running``). ``started`` continua sendo um state legado V2.
  if (state === "processing" || state === "running" || state === "started") {
    return "processing";
  }
  if (state === "stale") return "queued";
  if (state === "error" || state === "system_error" || state === "failed") {
    return "failed";
  }
  if (state === "pending" || state === "blocked") return "waiting";
  return "waiting";
}

export const BLOCK_BUCKET_LABEL = BLOCK_BUCKET_LABELS;
